import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type Column } from '../components/RecordsTable/RecordsTable';
import { ProductsCatalog, type SkuRow } from '../components/ProductsCatalog/ProductsCatalog';
import { ProductThumb } from '../components/ProductsCatalog/ProductThumb';
import { PriceEditModal, type PriceEditValues } from '../components/PriceEditModal/PriceEditModal';
import { useMcpTools } from '../lib/useMcpTools';
import { mcpWrite } from '../lib/mcp';
import { money, num, orDash } from '../lib/format';
import { useToast } from '../components/Toast/ToastProvider';
import './Products.css';

// SkuRow (incl. the client-side-joined `merv`) is defined in ProductsCatalog and imported above.
// NOTE: the MCP returns Postgres numeric columns (price/compare_at_price) as STRINGS (db.ts keeps
// OID 1700 as text to preserve precision), so they arrive as "30.00", not 30. money()/num() in
// lib/format do the coercion — never call .toFixed on these directly.

// The push-to-Shopify and sync-from-Shopify controls that used to live here were removed on
// 2026-09-03 on Mike's instruction. Shopify was cancelled by the strategic redirect and taken out
// of the code repo on 2026-08-28; the MCP no longer offers those actions, so the controls had
// been hidden by can() and were dead code. The catalog is authoritative on its own now: a price
// edited here IS the price, with nothing to push it to.

export function Products() {
  const queryClient = useQueryClient();
  const { can } = useMcpTools();
  const { notify } = useToast();
  const [editing, setEditing] = useState<SkuRow | null>(null);

  const canEditPrice = can('setSkuPrice');

  const refreshSkus = () => queryClient.invalidateQueries({ queryKey: ['mcp', 'listSkus'] });

  const setPrice = useMutation({
    mutationFn: (vars: { skuId: number; price: number; compareAtPrice: number | null }) =>
      mcpWrite('setSkuPrice', { skuId: vars.skuId, price: vars.price, compareAtPrice: vars.compareAtPrice }),
    onSuccess: (_data, vars) => {
      void refreshSkus();
      setEditing(null);
      notify({ message: `Price saved for SKU #${vars.skuId}.`, variant: 'success' });
    },
    onError: (err: Error) => notify({ message: err.message, variant: 'error' }),
  });

  const columns: Column<SkuRow>[] = [
    { key: 'image', header: '', render: (r) => <ProductThumb url={r.image_url} alt={r.sku_code} /> },
    { key: 'sku_code', header: 'SKU code' },
    { key: 'pack_count', header: 'Pack', numeric: true },
    { key: 'price', header: 'Price', numeric: true, render: (r) => money(r.price) },
    { key: 'upc', header: 'UPC', render: (r) => orDash(r.upc) },
    { key: 'amazon_asin', header: 'Amazon ASIN', render: (r) => orDash(r.amazon_asin) },
    { key: 'shopify_id', header: 'Shopify ID (historical)', render: (r) => orDash(r.shopify_id) },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <span className={`records__tag records__tag--${r.status === 'active' ? 'ok' : 'warn'}`}>{r.status}</span>
      ),
    },
  ];

  // Per-row action — edit price.
  const actions = canEditPrice
    ? {
        header: 'Actions',
        render: (row: SkuRow) => (
          <span className="products__row-actions">
            <button type="button" className="products__row-btn" onClick={() => setEditing(row)}>
              Edit price
            </button>
          </span>
        ),
      }
    : undefined;

  return (
    <>
      <ProductsCatalog
        title="Products"
        intro="The master catalog of sellable SKUs, grouped by MERV rating — each product's pack size, price, UPC and Amazon ASIN. Search or filter to narrow the catalog, or edit a SKU's price; the catalog is the system of record and nothing is pushed anywhere."
        columns={columns}
        rowKey={(r) => r.id}
        emptyMessage="No SKUs found."
        actions={actions}
      />

      {editing && (
        <PriceEditModal
          skuCode={editing.sku_code}
          currentPrice={num(editing.price)}
          currentCompareAt={num(editing.compare_at_price)}
          saving={setPrice.isPending}
          onClose={() => setEditing(null)}
          onSave={(vals: PriceEditValues) =>
            setPrice.mutate({ skuId: editing.id, price: vals.price, compareAtPrice: vals.compareAtPrice })
          }
        />
      )}
    </>
  );
}
